// Reglas de PRESENTACIÓN del puesto comercial (C2.1, slice 2A.1). Nada de esto decide precio,
// crédito ni stock: ordena y deriva lo que el backend ya entregó para mostrarlo en la cabecera, la
// situación comercial y el panel lateral. Lo que el backend aún no tiene queda marcado en el GAP
// report (docs/frontend/10-gap-puesto-comercial.md).
import type { Canal, FichaEntidad, MotivoCambioVendedor } from '../api/contratos';
import { NOMBRE_CONDICION } from '../comun/formato';

type Direccion = FichaEntidad['direcciones'][number];

/**
 * VISTA PREVIA del vencimiento (emisión + días) mientras el backend responde. El valor que vale es
 * el del backend (`cabecera.fechaVencimiento`, docs/frontend/12 §6). Fechas ISO, sin zona horaria.
 */
export function vencimiento(emisionIso: string, dias: number): string {
  const [a, m, d] = emisionIso.slice(0, 10).split('-').map(Number) as [number, number, number];
  const f = new Date(Date.UTC(a, m - 1, d + dias));
  return f.toISOString().slice(0, 10);
}

/** Hoy en Chile como `aaaa-mm-dd` (la fecha de negocio de una nota nueva la fija el backend). */
export function hoyIso(ahora = new Date()): string {
  return ahora.toLocaleDateString('sv-SE', { timeZone: 'America/Santiago' });
}

/**
 * Valor de una opción de condición: `contado`, `credito:30`, `cheque_a_plazo:15`. Las opciones
 * mismas (y el máximo) las entrega el backend en el contexto comercial: la interfaz no las decide.
 */
export function valorCondicion(condicion: string, plazo: number): string {
  return plazo > 0 ? `${condicion}:${plazo}` : condicion;
}

/** Texto de una opción: «Contado», «Crédito 30 días», «Cheque a plazo 15 días». */
export function textoCondicion(condicion: string, plazo: number): string {
  const nombre = NOMBRE_CONDICION[condicion] ?? condicion;
  return plazo > 0 ? `${nombre} ${plazo} días` : nombre;
}

/** Nombre de cada motivo del catálogo del backend (los motivos mismos vienen del contexto comercial). */
export const NOMBRE_MOTIVO_VENDEDOR: Record<MotivoCambioVendedor, string> = {
  DIGITACION_POR_VENDEDOR: 'Digitación por el vendedor',
  REASIGNACION_COMERCIAL: 'Reasignación comercial',
  VENTA_MESON: 'Venta en mesón',
  VENTA_ESPECIAL: 'Venta especial',
  OTRO: 'Otro (con observación)',
};

/**
 * ¿Elegir este vendedor pide motivo? Solo si no es el del cliente y cambia respecto de lo guardado
 * (la misma regla que aplica el backend; él es quien la exige).
 */
export function pideMotivoVendedor(
  elegido: string,
  delCliente: string | null,
  guardado: string | null,
): boolean {
  return !!elegido && elegido !== (delCliente ?? '') && elegido !== (guardado ?? '');
}

/** Dirección de despacho por defecto: despacho y principal; si no, la primera de despacho; si no, la principal. */
export function direccionPorDefecto(direcciones: readonly Direccion[]): Direccion | null {
  const activas = direcciones.filter((d) => d.activa);
  return (
    activas.find((d) => d.es_despacho && d.es_principal) ??
    activas.find((d) => d.es_despacho) ??
    activas.find((d) => d.es_principal) ??
    null
  );
}

/** Direcciones elegibles para despacho (las de despacho; si no hay ninguna marcada, todas las activas). */
export function direccionesDespacho(direcciones: readonly Direccion[]): Direccion[] {
  const activas = direcciones.filter((d) => d.activa);
  const despacho = activas.filter((d) => d.es_despacho);
  return despacho.length ? despacho : activas;
}

/**
 * Direcciones que se pueden elegir para despachar (ronda 1): TODAS las activas, la de despacho
 * principal primero, luego las de despacho y sucursales, y al final las demás habilitadas.
 */
export function direccionesElegibles(direcciones: readonly Direccion[]): Direccion[] {
  const peso = (d: Direccion) =>
    d.es_despacho && d.es_principal ? 0 : d.es_principal ? 1 : d.es_despacho ? 2 : 3;
  return direcciones.filter((d) => d.activa).sort((a, b) => peso(a) - peso(b));
}

/** «Principal», «Sucursal» (de despacho, no principal) u «Otra». */
export function tipoDireccion(d: Pick<Direccion, 'es_principal' | 'es_despacho'>): string {
  return d.es_principal ? 'Principal' : d.es_despacho ? 'Sucursal' : 'Otra';
}

/**
 * Canal de la nota según el ORIGEN (ronda 1): no se elige en la cabecera. El rol del usuario dice
 * desde dónde opera (call center, mesón); si no, vendedor. Queda en «Otros datos» y en la auditoría.
 */
export function canalPorDefecto(roles: readonly string[]): Canal {
  const r = roles.map((x) => x.toLowerCase());
  if (
    r.some(
      (x) => x.includes('callcenter') || x.includes('call_center') || x.includes('call center'),
    )
  )
    return 'CALL_CENTER';
  if (r.some((x) => x.includes('meson') || x.includes('mesón'))) return 'MESON';
  return 'VENDEDOR';
}

export function textoDireccion(d: Pick<Direccion, 'calle' | 'numero' | 'complemento'>): string {
  return [d.calle, d.numero, d.complemento].filter(Boolean).join(' ');
}

/** Contacto y teléfono principal del cliente (medios tipo teléfono, el principal primero). */
export function contactoPrincipal(f: Pick<FichaEntidad, 'contactos' | 'medios'>): {
  nombre: string | null;
  telefono: string | null;
} {
  const telefonos = f.medios.filter((m) => m.tipo === 'telefono' || m.tipo === 'whatsapp');
  const tel = telefonos.find((m) => m.principal) ?? telefonos[0] ?? null;
  const contacto =
    (tel?.contacto_id && f.contactos.find((c) => c.id === tel.contacto_id)) ||
    f.contactos.find((c) => c.activo) ||
    null;
  return { nombre: contacto ? contacto.nombre : null, telefono: tel?.valor ?? null };
}

/** El tramo que corresponde a una cantidad en unidad base (null si no llega a ninguno). */
export function tramoAplicado<T extends { cantidad_desde: string; cantidad_hasta: string | null }>(
  tramos: readonly T[],
  cantidadBase: number,
): T | null {
  return (
    tramos.find(
      (t) =>
        cantidadBase >= Number(t.cantidad_desde) &&
        (t.cantidad_hasta === null || cantidadBase < Number(t.cantidad_hasta)),
    ) ?? null
  );
}
